import { createClient } from "@supabase/supabase-js";
import { sendSMSNotification } from "./services/notificationService";
import { shouldRedirectToDashboard } from "./utils/authRedirectGuard";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

const BRAND_NAME = "LoadDash";

// ── SMS dedup: prevent repeat sends within 15 minutes ─────────
const _smsSentCache = new Map(); // key: "type:requestId" → timestamp
function shouldSendSMS(type, requestId) {
  const key = `${type}:${requestId}`;
  const lastSent = _smsSentCache.get(key);
  if (lastSent && Date.now() - lastSent < 15 * 60 * 1000) return false;
  _smsSentCache.set(key, Date.now());
  return true;
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Redirect to /dashboard on sign-out or missing session at startup.
// T2.3: decision extracted to a pure, tested predicate. Behavior is unchanged
// EXCEPT two bail-outs — never redirect while a Stripe return is in flight
// (?payment=... in the URL), and never redirect while an OAuth / email-link
// auth callback is in flight (params in the query or the hash). In both cases
// the callback params must reach the client / App.modular instead of being
// stripped by this full-page redirect.
supabase.auth.onAuthStateChange((event, session) => {
  if (shouldRedirectToDashboard({
    event,
    session,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  })) {
    window.location.href = '/dashboard';
  }
});

export async function signUp(email, password) {
  return await supabase.auth.signUp({ email, password });
}

export async function signIn(email, password) {
  return await supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  return await supabase.auth.signOut();
}

export async function signInWithGoogle(redirectTo) {
  return await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo },
  });
}

export async function getRole(session) {
  const { data } = await supabase.rpc('get_my_role');
  return data || 'resident';
}

export async function createRequest(requestData) {
  // LEU is now server-derived by enforce_booking_session_validity trigger.
  // Client must provide capture_session_id; trigger derives confirmed_leu from capture_sessions.leu.
  if (!requestData.capture_session_id) {
    throw new Error("SESSION_REQUIRED: Booking requires a valid capture session");
  }
  if (!requestData.size) {
    throw new Error("BOOKING_GUARD: canonical size/tier is required for request creation");
  }
  // Return the row we just inserted (with its payments relation) directly from
  // the INSERT, rather than re-fetching "newest by user_id" — which under a
  // double-tap / concurrent second booking could return a DIFFERENT request and
  // attach the payment to the wrong id. The auto-approval trigger runs BEFORE
  // INSERT, so the returned status is already the post-trigger value.
  const { data, error } = await supabase
    .from('requests')
    .insert([requestData])
    .select('*, payments(*)')
    .single();
  if (error) {
    console.error("[createRequest] INSERT failed", {
      message: error.message, details: error.details, hint: error.hint, code: error.code,
      context: { capture_session_id: requestData.capture_session_id, status: requestData.status, size: requestData.size, date: requestData.date, zone: requestData.zone },
    });
    throw error;
  }
  return data;
}

/** Persist capture session record */
export async function createCaptureSession(sessionData) {
  const { data, error } = await supabase.from('capture_sessions').insert([sessionData]).select().single();
  if (error) {
    console.debug("[CAPTURE_SESSION] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
    return null; // Non-fatal — session data is also kept client-side
  }
  return data;
}

/** Persist capture photo record */
export async function createCapturePhoto(photoData) {
  const { data, error } = await supabase.from('capture_photos').insert([photoData]).select().single();
  if (error) {
    console.debug("[CAPTURE_PHOTO] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
    return null; // Non-fatal
  }
  return data;
}

// ── Returning-customer identity stock (finding S10) ───────────────────────────
// Durable, cross-device contact/address memory, keyed to the account (not the device).
// The users table already carries these columns and self-scoped RLS
// (users_select_own_or_admin / users_update_own_or_admin / users_insert_self_or_admin),
// so no migration is required. Contact-only — never tier/LEU/price (invariant S8).

/** Read the caller's saved contact/address. Returns null on miss or error (non-fatal). */
export async function getUserContact(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('users')
    .select('default_address, pickup_notes, phone_number')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.debug('[userContact] read failed', error.message?.slice(0, 120));
    return null;
  }
  return data;
}

/**
 * Upsert the caller's saved contact/address. Fire-and-forget; never blocks a booking.
 * Only the three contact columns are written — no priced quantity ever passes through.
 */
export async function upsertUserContact(userId, contact = {}) {
  if (!userId) return null;
  const payload = { id: userId };
  if (contact.default_address != null) payload.default_address = contact.default_address;
  if (contact.pickup_notes != null) payload.pickup_notes = contact.pickup_notes;
  if (contact.phone_number != null) payload.phone_number = contact.phone_number;
  // Nothing to write beyond the primary key → skip.
  if (Object.keys(payload).length === 1) return null;
  const { data, error } = await supabase
    .from('users')
    .upsert([payload], { onConflict: 'id' })
    .select('default_address, pickup_notes, phone_number')
    .single();
  if (error) {
    console.debug('[userContact] upsert failed', error.message?.slice(0, 120));
    return null;
  }
  return data;
}

export async function getMyRequests(userId) {
  const { data, error } = await supabase.from('requests').select('*, payments(*)').eq('user_id', userId).order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getAllRequests() {
  const { data, error } = await supabase.from('requests').select('*, payments(*)').order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function updateRequestStatus(id, status, extraFields = {}) {
  const { data, error } = await supabase.from('requests').update({ status, ...extraFields }).eq('id', id).select();
  if (error) throw new Error('Supabase error: ' + error.message);
  if (!data || data.length === 0) throw new Error('RLS_BLOCKED');

  // TRIGGER C: Notify customer when driver is en_route (deduped per request)
  if (status === 'en_route' && data[0]) {
    const req = data[0];
    if (req.phone && req.sms_consent && shouldSendSMS("en_route_customer", id)) {
      try {
        // Look up driver first name if available
        let driverName = null;
        if (req.assigned_driver) {
          const { data: dp } = await supabase
            .from('driver_profiles')
            .select('first_name')
            .eq('user_id', req.assigned_driver)
            .single();
          driverName = dp?.first_name || null;
        }
        sendSMSNotification("en_route_customer", req.phone, {
          driver_name: driverName,
        }, id, "neighbor").catch(() => {});
      } catch (e) {
        console.debug("[updateRequestStatus] en_route notification failed (non-fatal):", e?.message);
      }
    }
  }

  return data;
}

export async function createPaymentRecord({ request_id, method, amount, status }) {
  const { data, error } = await supabase.from('payments').insert([{ request_id, method, amount, status: status || 'pending_verification' }]).select().single();
  if (error) throw error;
  return data;
}

export async function verifyManualPayment(requestId) {
  const { data, error } = await supabase.from('payments').update({ status: 'verified' }).eq('request_id', requestId);
  if (error) throw error;
  return data;
}

export async function getRequestPhotoOwnerId(userId) {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw new Error("Auth check failed: " + error.message);

  const sessionUserId = data?.user?.id;
  if (!sessionUserId) {
    throw new Error("Request photo upload requires an authenticated user");
  }
  if (userId && userId !== sessionUserId) {
    throw new Error("Request photo user mismatch");
  }
  return sessionUserId;
}

export async function uploadPhoto(file, userId) {
  if (!file) { console.error("[uploadPhoto] file is null/undefined"); return ""; }
  const ownerId = await getRequestPhotoOwnerId(userId);
  const ext = file.name ? file.name.split('.').pop() : 'jpg';
  const mime = file.type || 'image/jpeg';
  const path = `${ownerId}/requests/${crypto.randomUUID()}.${ext}`;
  try {
    const { error } = await supabase.storage.from('request-photos').upload(path, file, { upsert: true, contentType: mime });
    if (error) {
      console.error("[uploadPhoto] Storage error", { path, mime, fileSize: file?.size, statusCode: error.statusCode, message: error.message, error });
      if (error.message && (error.message.includes('Invalid Refresh Token') || error.message.includes('Refresh Token Not Found'))) {
        throw new Error('SESSION_EXPIRED');
      }
      throw error;
    }
  } catch (e) {
    if (e.message === 'SESSION_EXPIRED') throw e;
    if (e.message && (e.message.includes('Invalid Refresh Token') || e.message.includes('Refresh Token Not Found'))) {
      throw new Error('SESSION_EXPIRED');
    }
    throw e;
  }
  const { data: { publicUrl } } = supabase.storage.from('request-photos').getPublicUrl(path);
  return publicUrl;
}

export function calcCancellationFee(request) {
  const status = request.status;
  const pay = request.payments?.length ? [...request.payments].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] : null;
  const total = pay?.amount || 0;
  if (['complete','canceled_by_customer','canceled_by_admin','in_progress'].includes(status)) {
    return { canCancel: false, feePercent: 0, feeAmount: 0, refundAmount: 0 };
  }

  // Pickup window start time is parsed from the booking's pickup_window string (any Mon–Sat service day)
  // Fee window is 2 hours before pickup start
  const now = Date.now();
  let pickupStart = null;
  try {
    const pickupWindow = request.pickup_window || "";
    if (request.date) {
      const baseDate = new Date(request.date);
      if (!isNaN(baseDate.getTime())) {
        // Extract hour from pickup window string (e.g. "10:00 AM – 1:00 PM")
        const match = pickupWindow.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (match) {
          let hour = parseInt(match[1]);
          const min = parseInt(match[2]);
          const ampm = match[3].toUpperCase();
          if (ampm === "PM" && hour !== 12) hour += 12;
          if (ampm === "AM" && hour === 12) hour = 0;
          baseDate.setHours(hour, min, 0, 0);
          pickupStart = baseDate.getTime();
        }
      }
    }
  } catch { /* non-fatal — fall back to booking time */ }

  // Hours until pickup (negative = past pickup)
  const hoursUntilPickup = pickupStart ? (pickupStart - now) / (1000 * 60 * 60) : null;
  // Free window: more than 2 hours before pickup start
  const withinFreeWindow = hoursUntilPickup === null || hoursUntilPickup > 2;
  const minutesRemaining = hoursUntilPickup !== null ? Math.max(0, Math.round((hoursUntilPickup - 0) * 60)) : 0;

  let feePercent = 0;
  if (status === 'en_route') {
    feePercent = 60;
  } else if (['scheduled', 'available', 'claimed'].includes(status)) {
    feePercent = withinFreeWindow ? 0 : 20;
  } else if (['pending', 'checkout_pending', 'pending_priority', 'awaiting_priority_fallback'].includes(status)) {
    feePercent = 0; // pre-pool states are always free
  }
  const feeAmount = Math.round(total * feePercent / 100);
  const refundAmount = total - feeAmount;
  return { canCancel: true, feePercent, feeAmount, refundAmount, withinFreeWindow, minutesRemaining };
}

export async function cancelRequest({ requestId, reason, feePercent, feeAmount, refundAmount }) {
  const { data, error } = await supabase.from('requests').update({
    status: 'canceled_by_customer',
    cancellation_reason: reason || 'Customer requested cancellation',
    cancellation_fee_percent: feePercent,
    cancellation_fee_amount: feeAmount,
    refund_amount: refundAmount,
    refund_status: refundAmount > 0 ? 'pending' : 'none',
  }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function adminOverrideCancellation({ requestId, feePercent, feeAmount, refundAmount, overrideReason, adminId }) {
  const { data, error } = await supabase.from('requests').update({
    cancellation_fee_percent: feePercent,
    cancellation_fee_amount: feeAmount,
    refund_amount: refundAmount,
    refund_status: refundAmount > 0 ? 'pending' : 'none',
    override_reason: overrideReason,
    override_admin_id: adminId,
  }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function updateRefundStatus(requestId, refundStatus) {
  const { data, error } = await supabase.from('requests').update({ refund_status: refundStatus }).eq('id', requestId);
  if (error) throw error;
  return data;
}

export async function sendConfirmationEmail({ email, booking }, accessToken) {
  try {
    const { data, error } = await supabase.functions.invoke('send-confirmation', {
      body: { email, booking },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (error) throw error;
    return data;
  } catch (err) {
    console.log('Email send error (non-fatal):', err);
  }
}

export async function clearDeclinedRequests() {
  const { data, error } = await supabase.from('requests').update({ archived: true }).eq('status', 'declined');
  if (error) throw new Error(error.message);
  return data?.length || 0;
}

export async function clearArchivedRequests(adminId) {
  const { generateArchiveTag } = await import('./utils/businessTime');
  const { data: rows, error: fetchErr } = await supabase
    .from('requests')
    .select('id')
    .eq('archived', true)
    .eq('archive_cleared', false);
  if (fetchErr) throw new Error(fetchErr.message);
  if (!rows || rows.length === 0) return 0;
  const now = new Date().toISOString();
  for (const row of rows) {
    const tag = generateArchiveTag();
    await supabase.from('requests').update({
      archive_cleared: true,
      archive_tag_id: tag,
      archive_cleared_at: now,
      archive_cleared_by: adminId || null,
    }).eq('id', row.id);
  }
  return rows.length;
}

export async function voidPayment({ requestId, reason, adminId }) {
  const now = new Date().toISOString();
  const { error } = await supabase.from('payments').update({
    status: 'voided',
    void_reason: reason || 'Admin reversal',
    voided_at: now,
    voided_by: adminId || null,
  }).eq('request_id', requestId);
  if (error) throw new Error(error.message);
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

export async function completeJob({
  requestId,
  providerVerifiedLeu,
  confirmedStaging,
  flags,
  systemAssignedLeu,
  systemAssignedTier,
  driverVerifiedTier,
  completionPhotoUrl,
  completedBy,
  predictedLeuSnapshot,
  companyMarginAmt,
  driverPayAmt,
  variableCostAmt,
  marketId,
  zoneCostSnapshot,
}) {
  // LEU enforcement: provider-verified LEU is required for job completion
  if (providerVerifiedLeu == null) {
    throw new Error("VERIFIED_LEU_REQUIRED: Job completion requires verified LEU");
  }
  const now = new Date().toISOString();

  // Compute prediction variance at execution time
  const varianceLeu = systemAssignedLeu != null && providerVerifiedLeu != null
    ? providerVerifiedLeu - systemAssignedLeu
    : null;

  // Structured LEU logging
  if (varianceLeu != null && Math.abs(varianceLeu) > 1) {
    console.log("[LEU_MISMATCH]", JSON.stringify({ event: "LEU_MISMATCH", requestId, systemAssignedLeu, providerVerifiedLeu, delta: varianceLeu }));
  }

  const varianceTier = (systemAssignedTier && driverVerifiedTier && systemAssignedTier !== driverVerifiedTier)
    ? (providerVerifiedLeu > (systemAssignedLeu || 0) ? 'underreported' : 'overreported')
    : (systemAssignedTier && driverVerifiedTier ? 'match' : null);

  const { data, error } = await supabase
    .from('requests')
    .update({
      status:                     'complete',
      provider_verified_leu:      providerVerifiedLeu,
      prediction_variance_leu:    varianceLeu,
      prediction_variance_tier:   varianceTier,
      confirmed_staging_location: confirmedStaging,
      verified_at:                now,
      job_completed_at:           now,
      misrep_flagged:             flags.length > 0,
      data_quality_flags:         flags,
      driver_verified_tier:       driverVerifiedTier || null,
      driver_verified_at:         now,
      driver_issue_flags:         flags,
      completion_photo_url:       completionPhotoUrl || null,
      completed_at:               now,
      completed_by:               completedBy || null,
      predicted_leu_snapshot:     predictedLeuSnapshot ?? null,
      verification_source:        'driver_on_site',
      company_margin_amt:         companyMarginAmt ?? null,
      driver_pay_amt:             driverPayAmt ?? null,
      variable_cost_amt:          variableCostAmt ?? null,
      market_id:                  marketId,
      zone_cost_snapshot:         zoneCostSnapshot ?? null,
    })
    .eq('id', requestId)
    .select();

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('RLS_BLOCKED');

  // Write performance events (non-fatal — score visibility only)
  try {
    await supabase.rpc("record_job_completed_performance", { p_request_id: requestId });
    if (flags && flags.length > 0) {
      await supabase.rpc("record_issue_reported_performance", { p_request_id: requestId });
    }
  } catch (e) {
    console.debug("[completeJob] Performance event write failed (non-fatal):", e?.message);
  }

  return data;
}

/** Record a tier mismatch event (non-fatal) */
export async function recordTierMismatch({
  request_id = null,
  capture_session_id,
  system_assigned_tier,
  customer_selected_tier,
  override_direction,
  suggested_leu = null,
  confirmed_leu = null,
  composite_confidence_score = null,
}) {
  try {
    const { data, error } = await supabase.from('tier_mismatch_events').insert([{
      request_id,
      capture_session_id,
      system_assigned_tier,
      customer_selected_tier,
      override_direction,
      suggested_leu,
      confirmed_leu,
      composite_confidence_score,
      occurred_at: new Date().toISOString(),
    }]).select().single();
    if (error) {
      console.debug("[TIER_MISMATCH] insert_failed", { code: error.code, message: error.message?.slice(0, 120) });
      return null;
    }
    return data;
  } catch (e) {
    console.debug("[TIER_MISMATCH] insert_failed", { message: e?.message?.slice(0, 120) });
    return null;
  }
}

/** Load driver users from user_roles. Returns [{ user_id, role }] */
export async function getDriverUsers() {
  const { data, error } = await supabase.rpc('get_driver_list');
  if (error || !data) {
    // Fallback: try direct query (may fail due to RLS)
    const { data: d2, error: e2 } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'driver');
    if (e2 || !d2) return [];
    return d2.map(r => ({ value: r.user_id, label: r.user_id }));
  }
  return (data || []).map(r => ({ value: r.user_id, label: r.email || r.user_id }));
}

/** Fetch zone/date capacity totals (all users, read-only aggregate) */
export async function getZoneCapacity() {
  const { data, error } = await supabase.rpc('get_zone_capacity');
  if (error) { console.debug("[ZONE_CAPACITY] rpc_failed", error.message); return []; }
  return data || [];
}

/** Assign a driver to a request */
export async function assignDriver(requestId, driverUserId) {
  // Read current state for idempotency + notification data
  const { data: current } = await supabase
    .from('requests')
    .select('assigned_driver, driver_notified_at, addr, date, pickup_window, phone, sms_consent')
    .eq('id', requestId)
    .single();

  const { data, error } = await supabase
    .from('requests')
    .update({ assigned_driver: driverUserId })
    .eq('id', requestId)
    .select();
  if (error) throw error;

  // Notify on new assignment (null → driver, or driver A → driver B)
  if (driverUserId && driverUserId !== current?.assigned_driver) {
    try {
      // Look up driver profile for phone + name
      const { data: driverProfile } = await supabase
        .from('driver_profiles')
        .select('phone, first_name')
        .eq('user_id', driverUserId)
        .single();

      // TRIGGER A: Notify driver of new job (deduped per request)
      if (driverProfile?.phone && shouldSendSMS("driver_assigned", requestId)) {
        sendSMSNotification("driver_assigned", driverProfile.phone, {
          date: current?.date,
          window: current?.pickup_window,
          address: current?.addr,
        }, null, "hauler").catch(() => {});
      }

      // TRIGGER B: Notify customer that loader is assigned (deduped per request)
      if (current?.phone && current?.sms_consent && shouldSendSMS("loader_assigned_customer", requestId)) {
        const driverName = driverProfile?.first_name || null;
        sendSMSNotification("loader_assigned_customer", current.phone, {
          date: current?.date,
          window: current?.pickup_window,
          driver_name: driverName,
        }, requestId, "neighbor").catch(() => {});
      }

      await supabase.from('requests').update({ driver_notified_at: new Date().toISOString(), loader_first_name: driverProfile?.first_name || null }).eq('id', requestId);
    } catch (notifyErr) {
      console.debug("[assignDriver] Notification failed (non-fatal):", notifyErr?.message);
    }
  }

  return data;
}

/**
 * Driver self-claim: atomically claim an unassigned job.
 * Fails if already claimed by another driver (race-safe).
 * Sends customer notification on successful claim.
 */
export async function claimJob(requestId, driverUserId, { crewNames = [] } = {}) {
  // Atomic claim: only succeeds if status=available AND assigned_driver IS NULL
  // Server-side trigger (enforce_claim_validation) validates vehicle capacity,
  // trailer suppression, and crew names. Rejection raises CLAIM_REJECTED:<reason>.
  const updatePayload = { assigned_driver: driverUserId, status: 'claimed', crew_names: crewNames };

  const { data, error } = await supabase
    .from('requests')
    .update(updatePayload)
    .eq('id', requestId)
    .is('assigned_driver', null)
    .eq('status', 'available')
    .select('id, addr, date, pickup_window, phone, size, sms_consent');

  if (error) {
    const msg = error.message || '';
    if (msg.includes('CLAIM_REJECTED:')) {
      const reason = msg.split('CLAIM_REJECTED:')[1]?.split(/[\s"]/)[0] || 'unknown';
      throw new Error('CLAIM_REJECTED:' + reason);
    }
    throw new Error('Claim failed: ' + msg);
  }
  if (!data || data.length === 0) {
    throw new Error('JOB_ALREADY_CLAIMED');
  }

  const req = data[0];

  // Notify customer
  try {
    const { data: dp } = await supabase
      .from('driver_profiles')
      .select('phone, first_name')
      .eq('user_id', driverUserId)
      .single();

    if (req.phone && req.sms_consent && shouldSendSMS("loader_assigned_customer", requestId)) {
      sendSMSNotification("loader_assigned_customer", req.phone, {
        date: req.date,
        window: req.pickup_window,
        driver_name: dp?.first_name || null,
      }, requestId, "neighbor").catch(() => {});
    }

    // Notify driver via SMS (non-blocking)
    if (dp?.phone && shouldSendSMS("driver_assigned", requestId + "_driver")) {
      sendSMSNotification("driver_assigned", dp.phone, {
        address: req.addr,
        date: req.date,
        window: req.pickup_window,
      }, null, "hauler").catch(() => {});
    }

    await supabase.from('requests').update({ driver_notified_at: new Date().toISOString(), loader_first_name: dp?.first_name || null }).eq('id', requestId);
  } catch (e) {
    console.debug("[claimJob] Notification failed (non-fatal):", e?.message);
  }

  return data;
}

/**
 * Driver releases a claimed job back to the pool.
 * Only allowed while status = 'claimed' (before en_route).
 */
export async function releaseJob(requestId, driverUserId) {
  const { data, error } = await supabase
    .from('requests')
    .update({ assigned_driver: null, status: 'available', driver_notified_at: null })
    .eq('id', requestId)
    .eq('assigned_driver', driverUserId)
    .eq('status', 'claimed')
    .select('id');

  if (error) throw new Error('Release failed: ' + error.message);
  if (!data || data.length === 0) {
    throw new Error('JOB_NOT_RELEASABLE');
  }

  // Write performance event: cancel_after_accept (non-fatal)
  try {
    await supabase.rpc("record_cancel_after_accept_performance", { p_request_id: requestId });
  } catch (e) {
    console.debug("[releaseJob] Performance event write failed (non-fatal):", e?.message);
  }

  return data;
}

/** Admin release: return a stuck claimed/en_route job to the available pool */
export async function adminReleaseJob(requestId) {
  const { data, error } = await supabase
    .from('requests')
    .update({ status: 'available', assigned_driver: null, driver_notified_at: null })
    .eq('id', requestId)
    .in('status', ['claimed', 'en_route'])
    .select();
  if (error) throw error;
  if (!data?.length) throw new Error("Release failed: job not in claimed or en_route status");
  return data[0];
}

/** Record operator agreement signature (immutable — one row per version accepted) */
export async function recordAgreement() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { error } = await supabase.from("operator_agreements").insert([{
    user_id: user.id,
    agreed_at: new Date().toISOString(),
    agreement_version: "1.1",
  }]);
  if (error) throw error;
}

/** Check whether the current user has signed an operator agreement */
export async function hasOperatorAgreement() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data, error } = await supabase
    .from("operator_agreements")
    .select("id")
    .eq("user_id", user.id)
    .limit(1);
  if (error) return false;
  return data && data.length > 0;
}
